import React, { useState, useRef, useEffect } from "react"
import styled from "styled-components"

interface KuImageSliderProps {
  numOfImgs: number // Amount of images in total
  folderDir: string // Path to images
  baseNameFunction: (imgNum: number) => string // File basename
  ext: string // File extension
}

const Container = styled.div`
  width: 100%;
  height: 100%;
  max-width: 720px;
  min-width: 320px;
  margin: 0 auto;
`

const ImgContainer = styled.div`
  width: 100%;
  max-width: 540px;
  margin: 0 auto;
  padding: 0;
  cursor: ew-resize;
`

const ThreesixtyWrapper = styled.div`
  width: 100%;
  margin: 0;
  padding: 0;
  line-height: 0;
  position: relative;
`

const Img = styled.img`
  width: 100%;
  margin: 0;
  padding: 0;
  position: absolute;
  top: 0;
  left: 0;
  opacity: 0;

  &.active {
    opacity: 1;
  }
`

const KuImageSlider: React.FC<KuImageSliderProps> = ({ numOfImgs, folderDir, baseNameFunction, ext }) => {
  const [activeImageIndex, setActiveImageIndex] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const isSwipedRef = useRef(false)
  const initialXRef = useRef(0)

  const zeroPad = (num: number, places: number): string => {
    return String(num).padStart(places, "0")
  }

  const changeImg = (distance: number) => {
    const delta = 2 // Change this to change sensitivity on image change.
    const imgNum = activeImageIndex - Math.floor(distance / delta)

    // Ensure the new image index is within the valid range
    const newImageIndex = (imgNum + numOfImgs) % numOfImgs
    setActiveImageIndex(newImageIndex)
  }

  const handleTouchMove = (event: TouchEvent) => {
    if (isSwipedRef.current) {
      event.preventDefault()
      const clientX = event.touches[0].clientX
      const diffX = clientX - initialXRef.current
      changeImg(diffX)
    }
  }

  const handleTouchStart = (event: TouchEvent) => {
    isSwipedRef.current = true
    initialXRef.current = event.touches[0].clientX
  }

  const handleTouchEnd = () => {
    isSwipedRef.current = false
    initialXRef.current = 0
  }

  const handleMouseDown = (event: MouseEvent) => {
    event.preventDefault()
    isSwipedRef.current = true
    initialXRef.current = event.clientX
  }

  const handleMouseMove = (event: MouseEvent) => {
    if (isSwipedRef.current) {
      event.preventDefault()
      const diffX = event.clientX - initialXRef.current
      changeImg(diffX)
    }
  }

  const handleMouseUp = () => {
    isSwipedRef.current = false
    initialXRef.current = 0
  }

  useEffect(() => {
    const containerElement = containerRef.current

    if (containerElement) {
      containerElement.addEventListener("touchmove", handleTouchMove, {
        passive: false,
      })
      containerElement.addEventListener("touchstart", handleTouchStart)
      containerElement.addEventListener("touchend", handleTouchEnd)
      containerElement.addEventListener("mousedown", handleMouseDown)
      containerElement.addEventListener("mousemove", handleMouseMove)
      containerElement.addEventListener("mouseup", handleMouseUp)
      containerElement.addEventListener("mouseleave", handleMouseUp)

      return () => {
        containerElement.removeEventListener("touchmove", handleTouchMove)
        containerElement.removeEventListener("touchstart", handleTouchStart)
        containerElement.removeEventListener("touchend", handleTouchEnd)
        containerElement.removeEventListener("mousedown", handleMouseDown)
        containerElement.removeEventListener("mousemove", handleMouseMove)
        containerElement.removeEventListener("mouseup", handleMouseUp)
        containerElement.removeEventListener("mouseleave", handleMouseUp)
      }
    }
  }, [])

  // Preload images and get the viewer ready for the user
  const preloadImages = () => {
    const images = []
    for (let i = 0; i < numOfImgs; i++) {
      const imgPath = folderDir + baseNameFunction(i) + ext
      images.push(
        <Img
          key={i}
          decoding="async"
          className={`js-viewer ${i === activeImageIndex ? "active" : ""}`}
          src={imgPath}
          data-index={zeroPad(i, 4)}
          alt={`Image ${i}`}
        />,
      )
    }
    return images
  }

  return (
    <Container ref={containerRef}>
      <ImgContainer>
        <ThreesixtyWrapper>{preloadImages()}</ThreesixtyWrapper>
      </ImgContainer>
    </Container>
  )
}

export default KuImageSlider
